import { Fade, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { Badge, DirectionsCar, EvStation, LocalParking } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import {
  BookingType,
  NotSerializedBookingMode
} from "../../features/Booking-Form/typings/booking-inputs";
import {
  fitAndCorrectType,
  isSelectable,
  onModeChangeBySelectable,
  showTooltipBySelectable
} from "./functions/form-selection.functions";
import { StyledIconComponent } from "./partial/form-selection.partial";
import { ReactComponent as AvatarIcon } from "../../features/Booking-Form/assets/avatar.svg";
import { ReactComponent as ConferenceIcon } from "../../features/Booking-Form/assets/conference.svg";
import { ReactComponent as TeamIcon } from "../../features/Booking-Form/assets/group.svg";
import {
  getBookingTypeEnabled,
  type BookingTypeEnableMetaKey
} from "../../features/Admin/CompanyCard/SettingDialog/company-card-booking-type-enabling.config";
import type { MetaData } from "../../features/Login/typings/login.types";

export type ValidMaxAdvancedPerType = {
  workplace: boolean;
  parkingplace: boolean;
  electriccharging: boolean;
  conference: boolean;
  vehicle: boolean;
  visitor: boolean;
};

type P = {
  onModeChange: (mode: NotSerializedBookingMode) => void;
  iconSize?: { width: string; height: string };
  padding?: number;
  margin?: number;
  maxWidth?: string;
  titleVariant?: "h6" | "subtitle2";
  showTitle?: boolean;
  validMaxAdvancedPerType?: ValidMaxAdvancedPerType;
};

export function FormSelectionBookingType({
  onModeChange,
  iconSize = { width: "120px", height: "90px" },
  padding = 3,
  margin = 2,
  maxWidth = "300px",
  titleVariant = "h6",
  showTitle = true,
  validMaxAdvancedPerType
}: Readonly<P>) {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    userInformation: { placeTypes, zoneTypes, visitorManagementEnabled }
  } = useSelector((state: RootState) => state.login);
  const companyMeta = useSelector(
    (state: RootState) => state.login.userInformation.company.meta
  ) as MetaData;
  const metaBookingEnabled = (metaKey: BookingTypeEnableMetaKey) =>
    getBookingTypeEnabled(companyMeta, metaKey);

  const workplaceBookingEnabled = metaBookingEnabled("workplaceBookingEnabled");
  const parkingplaceBookingEnabled = metaBookingEnabled("parkingplaceBookingEnabled");
  const electricchargingplaceBookingEnabled = metaBookingEnabled(
    "electricchargingplaceBookingEnabled"
  );
  const conferencezoneBookingEnabled = metaBookingEnabled("conferencezoneBookingEnabled");
  const vehicleBookingEnabled = metaBookingEnabled("vehicleBookingEnabled");
  const visitorBookingEnabled = metaBookingEnabled("visitorBookingEnabled");
  const { teamBookingAllowed } = companyMeta;

  const paperSxProps = {
    cursor: "pointer",
    display: "flex",
    flexWrap: "wrap",
    boxSizing: "border-box",
    justifyContent: "center",
    flex: "1 1 100%",
    alignItems: "center",
    maxWidth: maxWidth,
    flexDirection: "column",
    padding: theme.spacing(padding),
    margin: theme.spacing(margin)
  };

  return (
    <>
      {workplaceBookingEnabled &&
        fitAndCorrectType(BookingType.WORKPLACE, placeTypes, zoneTypes) && (
          <Fade timeout={500} in>
            <Tooltip
              title={showTooltipBySelectable(BookingType.WORKPLACE, validMaxAdvancedPerType)}
            >
              <Paper
                onClick={() =>
                  onModeChangeBySelectable(
                    BookingType.WORKPLACE,
                    onModeChange,
                    validMaxAdvancedPerType
                  )
                }
                sx={{
                  ...paperSxProps,
                  ...isSelectable(BookingType.WORKPLACE, theme, validMaxAdvancedPerType)
                }}
                data-testid={"selection-booking-type-workplace"}
              >
                {showTitle && <Typography variant={titleVariant}>{t("Workplace")}</Typography>}
                <StyledIconComponent iconSize={iconSize}>
                  <AvatarIcon width={iconSize.width} height={iconSize.height} />
                </StyledIconComponent>
              </Paper>
            </Tooltip>
          </Fade>
        )}

      {workplaceBookingEnabled &&
        teamBookingAllowed &&
        fitAndCorrectType(BookingType.WORKPLACE, placeTypes, zoneTypes) && (
          <Fade timeout={800} in>
            <Tooltip title={showTooltipBySelectable(BookingType.TEAM, validMaxAdvancedPerType)}>
              <Paper
                onClick={() =>
                  onModeChangeBySelectable(BookingType.TEAM, onModeChange, validMaxAdvancedPerType)
                }
                sx={{
                  ...paperSxProps,
                  ...isSelectable(BookingType.TEAM, theme, validMaxAdvancedPerType)
                }}
                data-testid={"selection-booking-type-team"}
              >
                {showTitle && <Typography variant={titleVariant}>{t("Team")}</Typography>}
                <StyledIconComponent iconSize={iconSize}>
                  <TeamIcon width={iconSize.width} height={iconSize.height} />
                </StyledIconComponent>
              </Paper>
            </Tooltip>
          </Fade>
        )}

      {visitorManagementEnabled && visitorBookingEnabled && (
        <Fade timeout={900} in>
          <Tooltip title={showTooltipBySelectable(BookingType.VISITOR, validMaxAdvancedPerType)}>
            <Paper
              onClick={() =>
                onModeChangeBySelectable(BookingType.VISITOR, onModeChange, validMaxAdvancedPerType)
              }
              sx={{
                ...paperSxProps,
                ...isSelectable(BookingType.VISITOR, theme, validMaxAdvancedPerType)
              }}
              data-testid={"selection-booking-type-visitor"}
            >
              {showTitle && (
                <Typography variant={titleVariant}>{t("_bookingTypeVisitor")}</Typography>
              )}
              <StyledIconComponent iconSize={iconSize}>
                <Badge width={iconSize.width} height={iconSize.height} />
              </StyledIconComponent>
            </Paper>
          </Tooltip>
        </Fade>
      )}

      {conferencezoneBookingEnabled &&
        fitAndCorrectType(BookingType.CONFERENCEZONE, placeTypes, zoneTypes) && (
          <Fade timeout={1400} in>
            <Tooltip
              title={showTooltipBySelectable(BookingType.CONFERENCEZONE, validMaxAdvancedPerType)}
            >
              <Paper
                onClick={() =>
                  onModeChangeBySelectable(
                    BookingType.CONFERENCEZONE,
                    onModeChange,
                    validMaxAdvancedPerType
                  )
                }
                sx={{
                  ...paperSxProps,
                  ...isSelectable(BookingType.CONFERENCEZONE, theme, validMaxAdvancedPerType)
                }}
                data-testid={"selection-booking-type-conference"}
              >
                {showTitle && (
                  <Typography variant={titleVariant}>{t("_bookTypeMeetingRoom")}</Typography>
                )}
                <StyledIconComponent iconSize={iconSize}>
                  <ConferenceIcon width={iconSize.width} height={iconSize.height} />
                </StyledIconComponent>
              </Paper>
            </Tooltip>
          </Fade>
        )}

      {parkingplaceBookingEnabled &&
        fitAndCorrectType(BookingType.PARKINGPLACE, placeTypes, zoneTypes) && (
          <Fade timeout={1200} in>
            <Tooltip
              title={showTooltipBySelectable(BookingType.PARKINGPLACE, validMaxAdvancedPerType)}
            >
              <Paper
                onClick={() =>
                  onModeChangeBySelectable(
                    BookingType.PARKINGPLACE,
                    onModeChange,
                    validMaxAdvancedPerType
                  )
                }
                sx={{
                  ...paperSxProps,
                  ...isSelectable(BookingType.PARKINGPLACE, theme, validMaxAdvancedPerType)
                }}
                data-testid={"selection-booking-type-parkingPlace"}
              >
                {showTitle && <Typography variant={titleVariant}>{t("Parking")}</Typography>}
                <StyledIconComponent iconSize={iconSize}>
                  <LocalParking width={iconSize.width} height={iconSize.height} />
                </StyledIconComponent>
              </Paper>
            </Tooltip>
          </Fade>
        )}

      {electricchargingplaceBookingEnabled &&
        fitAndCorrectType(BookingType.ELECTRICCHARGINGSTATIONPLACE, placeTypes, zoneTypes) && (
          <Fade timeout={1300} in>
            <Tooltip
              title={showTooltipBySelectable(
                BookingType.ELECTRICCHARGINGSTATIONPLACE,
                validMaxAdvancedPerType
              )}
            >
              <Paper
                onClick={() =>
                  onModeChangeBySelectable(
                    BookingType.ELECTRICCHARGINGSTATIONPLACE,
                    onModeChange,
                    validMaxAdvancedPerType
                  )
                }
                sx={{
                  ...paperSxProps,
                  ...isSelectable(
                    BookingType.ELECTRICCHARGINGSTATIONPLACE,
                    theme,
                    validMaxAdvancedPerType
                  )
                }}
                data-testid={"selection-booking-type-electricchargingplace"}
              >
                {showTitle && (
                  <Typography variant={titleVariant}>{t("Electric Charging")}</Typography>
                )}
                <StyledIconComponent iconSize={iconSize}>
                  <EvStation width={iconSize.width} height={iconSize.height} />
                </StyledIconComponent>
              </Paper>
            </Tooltip>
          </Fade>
        )}

      {vehicleBookingEnabled && fitAndCorrectType(BookingType.VEHICLE, placeTypes, zoneTypes) && (
        <Fade timeout={1400} in>
          <Tooltip title={showTooltipBySelectable(BookingType.VEHICLE, validMaxAdvancedPerType)}>
            <Paper
              onClick={() =>
                onModeChangeBySelectable(BookingType.VEHICLE, onModeChange, validMaxAdvancedPerType)
              }
              sx={{
                ...paperSxProps,
                ...isSelectable(BookingType.VEHICLE, theme, validMaxAdvancedPerType)
              }}
              data-testid={"selection-booking-type-vehicle"}
            >
              {showTitle && (
                <Typography variant={titleVariant}>{t("_bookingTypeVehicle")}</Typography>
              )}
              <StyledIconComponent iconSize={iconSize}>
                <DirectionsCar width={iconSize.width} height={iconSize.height} />
              </StyledIconComponent>
            </Paper>
          </Tooltip>
        </Fade>
      )}
    </>
  );
}
